import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Checkbox, Divider, Form, Row, Typography } from "antd";
import styled from "styled-components";

import { FormDividerText } from "./styledComponents";
import { GRAY_300, GRAY_400, GRAY_700 } from "../../../../constant/colors";
import {
  selectCurrentApplication,
  selectExpressEnrollmentConfiguration,
  selectApplicationForm
} from "../../../../store/selector";
import { titleCase } from "../../../../utils/string";
import { fontWeights } from "../../../../constant/styles";
import IneligiblePatientWarning from "./IneligiblePatientWarning";
import { TEMPLATE_NAMES } from "../templateNames";

const { Text } = Typography;

export const assistanceTypeFormItem = "assistanceType";
export const adminAssistanceType = "assistance-type-admin";
export const drugAssistanceType = "assistance-type-drug";

const SupportText = styled(Text)`
  color: ${GRAY_700};
  font-weight: ${fontWeights.semibold};
`;

const FormItem = styled(Form.Item)`
  .ant-form-item-explain {
    min-height: 0px !important;
    height: 0;
  }
  margin-bottom: 0px;
`;

const normalizeState = (state) => {
  if (!state) return null;
  return state.split(" ")[0].toUpperCase();
};

export const SupportOptions = () => {
  const { t } = useTranslation();
  const expressEnrollmentConfiguration = useSelector(selectExpressEnrollmentConfiguration);
  const application = useSelector(selectCurrentApplication);
  const applicationForm = useSelector(selectApplicationForm);
  const drug = titleCase(application.drugDisplayName);

  const form = Form.useFormInstance();

  const currentPatientState = Form.useWatch(["patient", "state"], form);
  const questionnaireState = Form.useWatch(["eligibilityQuestionnaire", "residence-state"], form);

  const patientState = currentPatientState || applicationForm?.formData?.patient?.state;

  const isGenentechProgram =
    application?.ApplicationFormConfigurationTemplateName === TEMPLATE_NAMES.GENENTECH_COPAY ||
    application?.ApplicationFormConfigurationTemplateName === TEMPLATE_NAMES.GENENTECH_FREE_DRUG;

  // check for state mismatch between questionnaire and patient sections
  const normalizedPatientState = normalizeState(patientState);
  const normalizedQuestionnaireState = normalizeState(questionnaireState);
  const hasStateMismatch =
    normalizedPatientState && normalizedQuestionnaireState && normalizedPatientState !== normalizedQuestionnaireState;

  // only check for restricted state if there's no mismatch (prioritize data consistency)
  // Apply MA/RI restriction to ALL Genentech admin programs (regardless of drug)
  const isRestrictedState = !hasStateMismatch && (patientState === "MA" || patientState === "RI");
  const isAdminDisabled = isGenentechProgram && (hasStateMismatch || isRestrictedState);

  // custom validation rule for state mismatch - blocks submission but shows no message (we use visual warning instead)
  const stateMismatchValidator = {
    validator: async () => {
      if (hasStateMismatch) {
        return Promise.reject(new Error(""));
      }
      return Promise.resolve();
    }
  };

  // clear admin checkbox when restricted state is selected
  useEffect(() => {
    if (isAdminDisabled) {
      const currentAssistanceTypes = form.getFieldValue(assistanceTypeFormItem) || [];
      const hasAdminSelected = currentAssistanceTypes.includes(adminAssistanceType);

      if (hasAdminSelected) {
        const updatedAssistanceTypes = currentAssistanceTypes.filter((type) => type !== adminAssistanceType);
        form.setFieldsValue({ [assistanceTypeFormItem]: updatedAssistanceTypes });
      }
    }
  }, [isAdminDisabled, form]);

  // trigger validation when state fields change
  useEffect(() => {
    form.validateFields([assistanceTypeFormItem]);
  }, [hasStateMismatch, form]);

  return expressEnrollmentConfiguration?.hasAdminOption ? (
    <div id="enrollment-form-support-options">
      <Divider orientation="left" orientationMargin="0" style={{ color: GRAY_300 }}>
        <FormDividerText>{t("application_form_editor.genentech.copay.support_options.header")}</FormDividerText>
      </Divider>
      <SupportText>{t("application_form_editor.genentech.copay.support_options.title")}</SupportText>
      <FormItem rules={[{ required: true }, stateMismatchValidator]} name={assistanceTypeFormItem}>
        <Checkbox.Group>
          <Row>
            <Checkbox value={drugAssistanceType} disabled>
              <SupportText style={{ color: GRAY_400 }}>
                {t("application_form_editor.genentech.copay.support_options.checkbox_drug", { drug })}
              </SupportText>
            </Checkbox>
          </Row>
          <Row>
            <Checkbox value={adminAssistanceType} disabled={isAdminDisabled}>
              <SupportText style={{ color: isAdminDisabled ? GRAY_400 : GRAY_700 }}>
                {t("application_form_editor.genentech.copay.support_options.checkbox_admin", { drug })}
              </SupportText>
            </Checkbox>
          </Row>
        </Checkbox.Group>
      </FormItem>
      {/* Show state mismatch warning first (highest priority) */}
      {hasStateMismatch && (
        <IneligiblePatientWarning ineligibleAnswerError="application_form_editor.genentech.copay.support_options.state_mismatch_warning" />
      )}
      {/* Only show admin restriction when states match but it's MA/RI (applies to all Genentech drugs) */}
      {!hasStateMismatch && isRestrictedState && (
        <IneligiblePatientWarning ineligibleAnswerError="application_form_editor.genentech.copay.support_options.state_ineligibility_message" />
      )}
    </div>
  ) : null;
};
